import { Directive, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[positiveNumberOnly]'
})
export class PositiveNumberOnlyDirective {
  @Input() allowDecimal = false;
  @Input() decimalLimit: number | null = null;

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const selectionStart = input.selectionStart ?? 0;

    // Handle leading zeros prevention
    if (!this.allowDecimal) {
      // Prevent typing '0' when value is already '0'
      if (value === '0' && event.key === '0') {
        event.preventDefault();
        return;
      }
      // Replace leading zero with new number when typing digits 1-9
      if (value === '0' && /^[1-9]$/.test(event.key) && selectionStart === 1) {
        input.value = event.key;
        input.setSelectionRange(1, 1);
        event.preventDefault();
        return;
      }
    } else {
      // For decimal fields, allow '0.' but not '00', '000', etc.
      if (value === '0' && event.key === '0' && selectionStart <= 1) {
        event.preventDefault();
        return;
      }
      // Replace leading zero with new number when typing digits 1-9 (unless decimal follows)
      if (value === '0' && /^[1-9]$/.test(event.key) && selectionStart === 1) {
        input.value = event.key;
        input.setSelectionRange(1, 1);
        event.preventDefault();
        return;
      }
      // Prevent entering numbers after leading zero unless next is a decimal
      if (/^0[0-9]+/.test(value + event.key) && (!value.includes('.') || selectionStart <= value.indexOf('.'))) {
        event.preventDefault();
        return;
      }
    }
    if (this.allowDecimal) {
      const selectionStart = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? 0;
      // Simulate the new value if the key is accepted
      const newValue = value.substring(0, selectionStart) + event.key + value.substring(selectionEnd);
      if (this.decimalLimit !== null && newValue.includes('.')) {
        const decimalPart = newValue.split('.')[1] || '';
        if (decimalPart.length > this.decimalLimit) {
          event.preventDefault();
          return;
        }
      }
      if (event.key === '.') {
        // Prevent decimal as first character or on empty input
        if (input.selectionStart === 0 || value.length === 0) {
          event.preventDefault();
          return;
        }
        // Prevent more than one decimal unless replacing the existing one
        const existingDecimalIndex = value.indexOf('.');
        if (existingDecimalIndex !== -1 && (selectionStart > existingDecimalIndex || selectionEnd <= existingDecimalIndex)) {
          event.preventDefault();
          return;
        }
      }
      if (!/^[0-9.]$/.test(event.key)) {
        event.preventDefault();
      }
    } else {
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Clean up leading zeros after input
    if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
      const cleanedValue = value.replace(/^0+/, '') || '0';
      if (cleanedValue !== value) {
        input.value = cleanedValue;
        // Trigger input event to update form control
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const input = event.target as HTMLInputElement;
    const pastedInput: string = event.clipboardData?.getData('text/plain') ?? '';

    if (this.allowDecimal) {
      const match = pastedInput.match(/^\d*(\.\d*)?$/);
      if (!match) {
        event.preventDefault();
        return;
      }
      if (this.decimalLimit !== null && pastedInput.includes('.')) {
        const decimalPart = pastedInput.split('.')[1] || '';
        if (decimalPart.length > this.decimalLimit) {
          event.preventDefault();
          return;
        }
      }
    } else {
      if (!/^\d+$/.test(pastedInput)) {
        event.preventDefault();
        return;
      }
    }

    // Clean up leading zeros from pasted content
    setTimeout(() => {
      const value = input.value;
      if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
        const cleanedValue = value.replace(/^0+/, '') || '0';
        input.value = cleanedValue;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 0);
  }
}
